import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
    fetchAssignedProductsData,
    selectLoading,
    selectError,
    selectAllProducts,
    selectProducts,
    selectAllCompanies,
    selectAllCategories,
    selectBranches,
    selectSelectedBranch,
    selectSelectedCategory,
    selectSelectedCompany,
    selectFilteredProducts
} from '../store/slices/viewAssignedProductSlice';
import { saveAssignedProduct } from '../utilities/api';
import './CategoryMaster.css';

const emptyProduct = {
    spl_id: null,
    pr_id: '',
    basic_rate: 0,
    basic_tax_percent: 0,
    prcat_cat_id: '',
    company_id: '',
    branch_id: '',
    product_name: '',
    hsn_code: '',
    uom: '',
    company_name: ''
};

const taxOptions = [
    { label: '0%', value: 0 },
    { label: '5%', value: 5 },
    { label: '12%', value: 12 },
    { label: '18%', value: 18 },
    { label: '28%', value: 28 }
];


const ViewAssignedProductContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);
    const dt = useRef(null);

    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const allProducts = useSelector(selectAllProducts);
    const allCompanies = useSelector(selectAllCompanies);
    const allCategories = useSelector(selectAllCategories);
    const branches = useSelector(selectBranches);
    const products = useSelector(selectProducts);
    const selectedBranch = useSelector(selectSelectedBranch);
    const selectedCategory = useSelector(selectSelectedCategory);
    const selectedCompany = useSelector(selectSelectedCompany);
    const filteredProducts = useSelector(selectFilteredProducts);

    const [editCompanies, setEditCompanies] = useState([]);
    const [modalBranch, setModalBranch] = useState(null);
    const [modalCategory, setModalCategory] = useState(null);
    const [calcMode, setCalcMode] = useState('basic'); // 'basic' | 'mrp'
    const [mrpInput, setMrpInput] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState(emptyProduct);
    const [showDialog, setShowDialog] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (error) {
            toast.current?.show({ severity: 'error', summary: 'Load Error', detail: error, life: 5000 });
        }
    }, [error]);

    const reload = () => {
        dispatch(
            fetchAssignedProductsData({
                rolename: localStorage.getItem('role') || '',
                clientname: localStorage.getItem('clientid') || ''
            })
        );
    };

    const calculateRowValues = (rowData) => {
        const basic = parseFloat(rowData.basic_rate || 0);
        const taxPercent = parseFloat(rowData.basic_tax_percent || 0);
        const taxAmount = (basic * taxPercent) / 100;
        const sgst = taxAmount / 2;
        const cgst = taxAmount / 2;
        const total = basic + taxAmount;
        return { basic, taxPercent, taxAmount, sgst, cgst, total };
    };

    const openNew = () => {
        const initBranch = selectedBranch || null;
        const initCategory = selectedCategory || null;
        const initCompany = selectedCompany || null;
        setModalBranch(initBranch);
        setModalCategory(initCategory);
        setSelectedProduct({
            ...emptyProduct,
            prcat_cat_id: initCategory?.code || '',
            company_id: initCompany?.company_id || '',
            branch_id: initBranch?.branch_id || '',
            company_name: initCompany?.company_name || ''
        });
        setEditCompanies(allCompanies.map((c) => ({ company_id: c.company_id, company_name: c.company_name })));
        setCalcMode('basic');
        setMrpInput(0);
        setSubmitted(false);
        setShowDialog(true);
    };

    const editProduct = (product) => {
        const branch = branches.find((b) => String(b.branch_id) === String(product.branch_id)) || null;
        const category = allCategories.find((c) => String(c.code) === String(product.prcat_cat_id)) || null;
        setModalBranch(branch);
        setModalCategory(category);
        setSelectedProduct({
            ...product,
            basic_rate: parseFloat(product.basic_rate) || 0,
            basic_tax_percent: parseFloat(product.basic_tax_percent) || 0
        });
        setEditCompanies(allCompanies.map((c) => ({ company_id: c.company_id, company_name: c.company_name })));
        setCalcMode('basic');
        const basic = parseFloat(product.basic_rate) || 0;
        const tax = parseFloat(product.basic_tax_percent) || 0;
        setMrpInput(parseFloat((basic * (1 + tax / 100)).toFixed(2)));
        setSubmitted(false);
        setShowDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setShowDialog(false);
        setSelectedProduct(emptyProduct);
        setEditCompanies([]);
        setModalBranch(null);
        setModalCategory(null);
        setCalcMode('basic');
        setMrpInput(0);
    };

    const saveProduct = () => {
        setSubmitted(true);

        if (!selectedProduct.branch_id) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Branch is required' });
            return;
        }
        if (!selectedProduct.prcat_cat_id) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Category is required' });
            return;
        }
        if (!selectedProduct.product_name?.trim()) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Product name is required' });
            return;
        }
        if (!selectedProduct.hsn_code?.trim()) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'HSN code is required' });
            return;
        }
        if (!selectedProduct.uom?.trim()) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'UOM is required' });
            return;
        }
        if (!selectedProduct.company_id) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Company is required' });
            return;
        }
        if (calcMode === 'mrp' && mrpInput <= 0) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'MRP must be greater than 0' });
            return;
        }
        if (calcMode === 'basic' && selectedProduct.basic_rate <= 0) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Basic rate must be greater than 0' });
            return;
        }

        confirmDialog({
            message: selectedProduct.spl_id
                ? `Are you sure you want to update "${selectedProduct.product_name}"?`
                : `Are you sure you want to add "${selectedProduct.product_name}"?`,
            header: selectedProduct.spl_id ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleSave()
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const calc = calculateRowValues(selectedProduct);
            const payload = {
                spl_id: selectedProduct.spl_id || null,
                pr_id: selectedProduct.pr_id || 0,
                prcat_cat_id: selectedProduct.prcat_cat_id,
                company_id: selectedProduct.company_id,
                branch_id: selectedProduct.branch_id,
                product_name: selectedProduct.product_name.trim(),
                hsn_code: selectedProduct.hsn_code.trim(),
                uom: selectedProduct.uom.trim(),
                basic_rate: calc.basic,
                basic_tax_percent: calc.taxPercent,
                basic_tax_amount: calc.taxAmount,
                sgst_tax: calc.taxPercent / 2,
                sgst_tax_amount: calc.sgst,
                cgst_tax: calc.taxPercent / 2,
                cgst_tax_amount: calc.cgst,
                price: calc.total,
                product_status: 'Active',
                client_id: localStorage.getItem('clientid'),
                created_by: localStorage.getItem('userid')
            };

            const resData = await saveAssignedProduct(payload);
            if (resData?.status) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: resData.message || (selectedProduct.spl_id ? 'Product updated successfully' : 'Product added successfully'),
                    life: 3000
                });
                setShowDialog(false);
                setSelectedProduct(emptyProduct);
                reload();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: resData?.message || 'Save failed', life: 5000 });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Connection Error', detail: err.message, life: 5000 });
        } finally {
            setSaving(false);
        }
    };

    const exportExcel = async () => {
        if (filteredProducts.length === 0) {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'No data to export' });
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Products');
        sheet.addRow([
            `Branch: ${selectedBranch?.branch_name || 'All'}`,
            `Category: ${selectedCategory?.name || 'All'}`,
            `Company: ${selectedCompany?.company_name || 'All'}`
        ]);
        sheet.addRow(['Product Name', 'Company', 'HSN Code', 'UOM', 'Basic Rate', 'Tax %', 'SGST', 'CGST', 'Tax Amount', 'Total', 'Branch']);
        const rows = dt.current?.filteredValue || filteredProducts;
        rows.forEach((r) => {
            const v = calculateRowValues(r);
            sheet.addRow([r.product_name, r.company_name, r.hsn_code, r.uom, v.basic.toFixed(2), v.taxPercent.toFixed(2), v.sgst.toFixed(2), v.cgst.toFixed(2), v.taxAmount.toFixed(2), v.total.toFixed(2), r.branch_name]);
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'AssignedProducts.xlsx');
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Data exported successfully' });
    };

    const onInputNumberChange = (e, name) => {
        setSelectedProduct({ ...selectedProduct, [name]: e.value || 0 });
    };

    const onProductChange = (e) => {
        const pr = allProducts.find((p) => String(p.pr_id) === String(e.value));
        if (pr) {
            setSelectedProduct({
                ...selectedProduct,
                pr_id: e.value,
                product_name: pr.product_name || '',
                hsn_code: pr.hsn_code || '',
                uom: pr.uom || '',
                basic_rate: parseFloat(pr.basic_rate) || 0,
                basic_tax_percent: parseFloat(pr.basic_tax_percent) || 0
            });
        }
    };

    const onCompanyChange = (e) => {
        const comp = editCompanies.find((c) => String(c.company_id) === String(e.value));
        setSelectedProduct({ ...selectedProduct, company_id: e.value, company_name: comp?.company_name || '' });
    };

    const onModalBranchChange = (branch) => {
        setModalBranch(branch);
        setModalCategory(null);
        setSelectedProduct((prev) => ({ ...prev, branch_id: branch?.branch_id || '', prcat_cat_id: '' }));
    };

    const onModalCategoryChange = (cat) => {
        setModalCategory(cat);
        setSelectedProduct((prev) => ({ ...prev, prcat_cat_id: cat?.code || '' }));
    };

    const modalFilteredCategories = modalBranch
        ? allCategories.filter((c) => String(c.branch_id) === String(modalBranch.branch_id))
        : [];

    const onCalcModeChange = (mode) => {
        if (mode === 'mrp') {
            const basic = parseFloat(selectedProduct.basic_rate) || 0;
            const tax = parseFloat(selectedProduct.basic_tax_percent) || 0;
            setMrpInput(parseFloat((basic * (1 + tax / 100)).toFixed(2)));
        }
        setCalcMode(mode);
    };

    const onMrpChange = (value) => {
        const mrp = value || 0;
        setMrpInput(mrp);
        const tax = parseFloat(selectedProduct.basic_tax_percent) || 0;
        const basicRate = mrp / (1 + tax / 100);
        setSelectedProduct((prev) => ({ ...prev, basic_rate: basicRate }));
    };

    const onTaxChange = (taxValue) => {
        if (calcMode === 'mrp') {
            const tax = taxValue || 0;
            const basicRate = mrpInput / (1 + tax / 100);
            setSelectedProduct((prev) => ({ ...prev, basic_tax_percent: taxValue, basic_rate: basicRate }));
        } else {
            setSelectedProduct((prev) => ({ ...prev, basic_tax_percent: taxValue }));
        }
    };

    const onInputChange = (e, name) => {
        setSelectedProduct({ ...selectedProduct, [name]: e.target.value });
    };

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => editProduct(rowData)}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-success" onClick={saveProduct} loading={saving} />
        </div>
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Special Products List</h3>
                    <span className="text-color-secondary text-sm">({filteredProducts.length} of {products.length} records)</span>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search products..."
                            className="w-full"
                        />
                    </span>
                    <Button label="Add New" icon="pi pi-plus" className="p-button-primary" onClick={openNew} />
                    <Button label="Export" icon="pi pi-download" className="p-button-success" onClick={exportExcel} />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <DataTable
                    ref={dt}
                    value={filteredProducts}
                    filters={filters}
                    globalFilterFields={['product_name', 'company_name', 'hsn_code', 'uom']}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                    showGridlines
                    tableStyle={{ minWidth: '60rem' }}
                    className="p-datatable-sm"
                    emptyMessage="Please select Branch, Category and Company to view products"
                >
                    <Column field="product_name" header="Product Name" sortable />
                    <Column field="company_name" header="Company / Order Type" sortable style={{ width: '160px' }} />
                    <Column field="hsn_code" header="HSN Code" sortable style={{ width: '120px' }} />
                    <Column field="uom" header="UOM" sortable style={{ width: '80px' }} />
                    <Column field="basic_rate" header="Basic Rate" body={(r) => calculateRowValues(r).basic.toFixed(2)} sortable style={{ width: '110px' }} />
                    <Column field="basic_tax_percent" header="Tax (%)" body={(r) => calculateRowValues(r).taxPercent.toFixed(2)} sortable style={{ width: '90px' }} />
                    <Column header="SGST" body={(r) => calculateRowValues(r).sgst.toFixed(2)} style={{ width: '90px' }} />
                    <Column header="CGST" body={(r) => calculateRowValues(r).cgst.toFixed(2)} style={{ width: '90px' }} />
                    <Column header="Total" body={(r) => calculateRowValues(r).total.toFixed(2)} sortable style={{ width: '100px' }} />
                    <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                </DataTable>
            )}

            <Dialog
                visible={showDialog}
                style={{ width: '600px' }}
                header={selectedProduct.spl_id ? 'Edit Product' : 'Add Product'}
                modal
                className="p-fluid"
                footer={dialogFooter}
                onHide={hideDialog}
            >
                <div className="flex flex-column" style={{ gap: '0.6rem' }}>
                    {/* Company */}
                    <div className="field mb-0">
                        <label htmlFor="company_id" className="mb-1 block text-sm font-medium">Company <span style={{ color: 'red' }}>*</span></label>
                        <Dropdown
                            id="company_id"
                            value={selectedProduct.company_id}
                            options={editCompanies.filter((c) => ['ODC', 'Walk-In'].includes(c.company_name))}
                            onChange={onCompanyChange}
                            optionLabel="company_name"
                            optionValue="company_id"
                            placeholder="Select Company"
                            filter
                            className={submitted && !selectedProduct.company_id ? 'p-invalid' : ''}
                        />
                        {submitted && !selectedProduct.company_id && <small className="p-error">Company is required.</small>}
                    </div>

                    {/* Branch & Category */}
                    <div className="grid" style={{ margin: 0 }}>
                        <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                            <div className="field mb-0">
                                <label className="mb-1 block text-sm font-medium">Branch <span style={{ color: 'red' }}>*</span></label>
                                <Dropdown
                                    value={modalBranch}
                                    options={branches}
                                    onChange={(e) => onModalBranchChange(e.value)}
                                    optionLabel="branch_name"
                                    placeholder="Select Branch"
                                    filter
                                    disabled={!!selectedProduct.spl_id}
                                    className={submitted && !selectedProduct.branch_id ? 'p-invalid' : ''}
                                />
                                {submitted && !selectedProduct.branch_id && <small className="p-error">Branch is required.</small>}
                            </div>
                        </div>
                        <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                            <div className="field mb-0">
                                <label className="mb-1 block text-sm font-medium">Category <span style={{ color: 'red' }}>*</span></label>
                                <Dropdown
                                    value={modalCategory}
                                    options={modalFilteredCategories}
                                    onChange={(e) => onModalCategoryChange(e.value)}
                                    optionLabel="name"
                                    placeholder={modalBranch ? 'Select Category' : 'Select Branch First'}
                                    filter
                                    disabled={!!selectedProduct.spl_id || !modalBranch}
                                    className={submitted && !selectedProduct.prcat_cat_id ? 'p-invalid' : ''}
                                />
                                {submitted && !selectedProduct.prcat_cat_id && <small className="p-error">Category is required.</small>}
                            </div>
                        </div>
                    </div>

                    {/* Product Name */}
                    <div className="field mb-0">
                        <label htmlFor="product_name" className="mb-1 block text-sm font-medium">Product Name <span style={{ color: 'red' }}>*</span></label>
                        <InputText
                            id="product_name"
                            value={selectedProduct.product_name}
                            onChange={(e) => onInputChange(e, 'product_name')}
                            placeholder="Enter product name"
                            className={submitted && !selectedProduct.product_name?.trim() ? 'p-invalid' : ''}
                        />
                        {submitted && !selectedProduct.product_name?.trim() && <small className="p-error">Product name is required.</small>}
                    </div>

                    {/* HSN Code & UOM */}
                    <div className="grid" style={{ margin: 0 }}>
                        <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                            <div className="field mb-0">
                                <label htmlFor="hsn_code" className="mb-1 block text-sm font-medium">HSN Code <span style={{ color: 'red' }}>*</span></label>
                                <InputText
                                    id="hsn_code"
                                    value={selectedProduct.hsn_code}
                                    onChange={(e) => onInputChange(e, 'hsn_code')}
                                    placeholder="Enter HSN code"
                                    className={submitted && !selectedProduct.hsn_code?.trim() ? 'p-invalid' : ''}
                                />
                                {submitted && !selectedProduct.hsn_code?.trim() && <small className="p-error">HSN code is required.</small>}
                            </div>
                        </div>
                        <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                            <div className="field mb-0">
                                <label htmlFor="uom" className="mb-1 block text-sm font-medium">UOM <span style={{ color: 'red' }}>*</span></label>
                                <InputText
                                    id="uom"
                                    value={selectedProduct.uom}
                                    onChange={(e) => onInputChange(e, 'uom')}
                                    placeholder="Enter UOM"
                                    className={submitted && !selectedProduct.uom?.trim() ? 'p-invalid' : ''}
                                />
                                {submitted && !selectedProduct.uom?.trim() && <small className="p-error">UOM is required.</small>}
                            </div>
                        </div>
                    </div>

                    {/* Calculation mode toggle */}
                    <div className="field mb-0">
                        <label className="mb-1 block text-sm font-medium">Price Calculation Mode</label>
                        <div className="flex gap-2 mt-1">
                            <Button
                                label="Forward  (Basic → MRP)"
                                icon="pi pi-arrow-right"
                                type="button"
                                size="small"
                                className={calcMode === 'basic' ? 'p-button-primary' : 'p-button-outlined p-button-secondary'}
                                onClick={() => onCalcModeChange('basic')}
                            />
                            <Button
                                label="Reverse  (MRP → Basic)"
                                icon="pi pi-arrow-left"
                                type="button"
                                size="small"
                                className={calcMode === 'mrp' ? 'p-button-primary' : 'p-button-outlined p-button-secondary'}
                                onClick={() => onCalcModeChange('mrp')}
                            />
                        </div>
                    </div>

                    {calcMode === 'basic' ? (
                        <>
                            <div className="grid" style={{ margin: 0 }}>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label htmlFor="basic_rate" className="mb-1 block text-sm font-medium">Basic Rate <span style={{ color: 'red' }}>*</span></label>
                                        <InputNumber
                                            id="basic_rate"
                                            value={selectedProduct.basic_rate}
                                            onValueChange={(e) => onInputNumberChange(e, 'basic_rate')}
                                            mode="decimal"
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            min={0}
                                            className={submitted && selectedProduct.basic_rate <= 0 ? 'p-invalid' : ''}
                                        />
                                        {submitted && selectedProduct.basic_rate <= 0 && <small className="p-error">Basic rate must be greater than 0.</small>}
                                    </div>
                                </div>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label htmlFor="basic_tax_percent" className="mb-1 block text-sm font-medium">Tax %</label>
                                        <Dropdown
                                            id="basic_tax_percent"
                                            value={selectedProduct.basic_tax_percent}
                                            options={taxOptions}
                                            onChange={(e) => onTaxChange(e.value)}
                                            placeholder="Select Tax %"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="grid" style={{ margin: 0 }}>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">SGST (Auto)</label>
                                        <InputText value={(((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 200).toFixed(2)} disabled />
                                    </div>
                                </div>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">CGST (Auto)</label>
                                        <InputText value={(((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 200).toFixed(2)} disabled />
                                    </div>
                                </div>
                            </div>
                            <div className="grid" style={{ margin: 0 }}>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">Tax Amount (Auto)</label>
                                        <InputText value={(((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 100).toFixed(2)} disabled />
                                    </div>
                                </div>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">MRP / Total (Auto)</label>
                                        <InputText value={((parseFloat(selectedProduct.basic_rate) || 0) + ((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 100).toFixed(2)} disabled />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid" style={{ margin: 0 }}>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label htmlFor="mrp_input" className="mb-1 block text-sm font-medium">MRP / Total Price <span style={{ color: 'red' }}>*</span></label>
                                        <InputNumber
                                            id="mrp_input"
                                            value={mrpInput}
                                            onValueChange={(e) => onMrpChange(e.value)}
                                            mode="decimal"
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            min={0}
                                            className={submitted && mrpInput <= 0 ? 'p-invalid' : ''}
                                        />
                                        {submitted && mrpInput <= 0 && <small className="p-error">MRP must be greater than 0.</small>}
                                    </div>
                                </div>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label htmlFor="basic_tax_percent_mrp" className="mb-1 block text-sm font-medium">Tax %</label>
                                        <Dropdown
                                            id="basic_tax_percent_mrp"
                                            value={selectedProduct.basic_tax_percent}
                                            options={taxOptions}
                                            onChange={(e) => onTaxChange(e.value)}
                                            placeholder="Select Tax %"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="grid" style={{ margin: 0 }}>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">Basic Rate (Auto)</label>
                                        <InputText value={(parseFloat(selectedProduct.basic_rate) || 0).toFixed(2)} disabled />
                                    </div>
                                </div>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">Tax Amount (Auto)</label>
                                        <InputText value={(((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 100).toFixed(2)} disabled />
                                    </div>
                                </div>
                            </div>
                            <div className="grid" style={{ margin: 0 }}>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">SGST (Auto)</label>
                                        <InputText value={(((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 200).toFixed(2)} disabled />
                                    </div>
                                </div>
                                <div className="col-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <div className="field mb-0">
                                        <label className="mb-1 block text-sm font-medium">CGST (Auto)</label>
                                        <InputText value={(((parseFloat(selectedProduct.basic_rate) || 0) * (parseFloat(selectedProduct.basic_tax_percent) || 0)) / 200).toFixed(2)} disabled />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        </Card>
    );
};

export default ViewAssignedProductContent;
